import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import { formatAmount, formatDelta, tierClassName } from '../utils/refundFormat';

/**
 * v0.18.62 (辉哥 10/23 拍板) — **退卡申请已提交** modal (设计稿 v4 · S3)
 *
 * 视觉基线: `public/assets/ziven-scheduler-refund-mockup.html` 的 `?scene=result`
 *
 * ## 数据来源 (⛔ 一个数都不许前端自己算)
 *   · `最终打款金额` = `result.meta.amount` (**最终应付总额**, 手填了就用手填的)
 *   · `系统核算金额` = `result.meta.system_amount`
 *   · `退费原因`     = `result.meta.reason_label`
 *   · `审批档`       = `result.meta.approval_label` + `result.meta.tier_order`
 *     (⛔ 前端**不建** tier → 文案 / tier → tier-N 映射表)
 *   · `发起时间`     = `result.refund_at` (后端落库时写的那个时间戳)
 *   · `关联卡`       = 卡对象上的 卡名 / 学员名 / 剩余课时
 *   · `差额`         = `meta.amount − meta.system_amount` (**两个都是后端下发的数**,
 *     这里只做展示差值; ⛔ 不许重算退费公式、不许判档)
 *
 * ⚠️ **最终打款金额用 `meta.amount`** (不是 `refund_amount`): 后端把总额拆成
 *   principal / interest / fee 三列落库, `meta.amount` 才是「最终应付总额」这个口径。
 *
 * ## 收据副标题 + 差额行 (设计稿 L1144-1157, 两个变体)
 *   · 无差额 (`amount == system_amount`) ⇒ 「系统核算 ¥X，最终以填写金额为准」, **不渲染**差额行
 *   · 有差额 ⇒ 「系统核算 ¥X · 多退 ¥Y」(申请额 > 系统额) / 「系统核算 ¥X · 少退 ¥Y」(申请额 < 系统额),
 *     并在「系统核算金额」行**下面**插入「差额」行 (chip = `.ZS-RefundModal-delta`, 带 `fa-circle-exclamation` 图标)
 *
 * ## ⭐⭐ v0.18.76 (D5) — 审批通过后的**回执页** (加一个 `variant` 变体)
 *
 * ### 两条数据路径**不许混用** (本仓铁律: 一个逻辑一个入口)
 *   | | `variant='request'` (默认) | `variant='approve'` (v0.18.76 新增) |
 *   |---|---|---|
 *   | 触发 | `RefundRequestModal.submit()` (L507, **没传 variant**) | `RefundApproveModal.approve()` |
 *   | 数据源 | **`result.meta.*`** | **平铺的 `result.*`** |
 *   | 标题 | 「退卡申请已提交」 | 「退卡已通过」 |
 *   | 差额 | `requestDelta()` (读 `meta.*`) | `approveDelta()` (读**平铺**的 `system_amount` / `refund_amount`) |
 *   | 学员名 | `card.student_display_name` | `result.student_name` (审批场景前端拿不到卡上的学员名) |
 *   | 卡名 | `card.template_name` | `result.card_name` (后端给) |
 *   | 时间行 | 「发起时间」`result.refund_at` | 「审批时间」`result.refund_settled_at` |
 *
 *   ⛔ `request` 变体的**读法一个字都不许改** (它服务存量的「申请已提交」流程)。
 *   ⛔ **不许**复用 `requestDelta()` 给 approve 用 (它读的是 `meta.*` —— 另一条路径)。
 *   ⛔ `variant` 的**默认值必须是 `'request'`** —— 生产 `RefundRequestModal` 调用时**没有传** variant。
 *   ⇒ 实现上: `oninit()` 里按 variant 选**一份字段映射** (`requestView()` / `approveView()`),
 *     `render` 里**不写**任何 `variant === 'approve' ? a : b` (本仓铁律: 一个逻辑一个入口)。
 *
 * ### ⚠️ 映射里只放**纯数据**, 不放 JSX
 *   Mithril 2 会把 `dom` / `domSize` 写在 **vnode 对象**上 (fragment 还会累加 `domSize`),
 *   而 redraw 时它拿「新 vnode」和「旧 vnode」比 —— 同一个 vnode 对象被复用两次
 *   ⇒ 会命中 Mithril 的 "Don't reuse attrs object" 警告, 且 fragment 的 `domSize` 会重复累加。
 *   ⇒ **所有 JSX 都在 render 里现建** (映射只给纯数据 + 一个 `kind` 判别式)。
 */
export default class RefundResultModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.card = vnode.attrs.card || {};
    this.result = vnode.attrs.result || {};

    // ⚠️ v0.18.76 — 变体。⛔ **默认值必须是 'request'**:
    //   生产 `RefundRequestModal.submit()` 的调用 (L507) **没有传 variant**
    //   ⇒ 不传时必须走**原路径** (读 `result.meta.*`), 否则存量流程的弹窗会变样。
    this.variant = vnode.attrs.variant === 'approve' ? 'approve' : 'request';

    // ⭐ 按变体选**一份字段映射** —— render 里不再出现任何 variant 三元表达式。
    //   ⚠️ 字段名**不能叫 `this.view`** —— Mithril 的组件基类有一个 `view()` 方法
    //   (在原型上), 一旦被实例字段遮蔽成普通对象, Mithril 渲染时就会炸
    //   「TypeError: this.apply is not a function」⇒ 整个弹窗都渲染不出来。
    this.fields = this.variant === 'approve' ? this.approveView() : this.requestView();
  }
  className() {
    return 'Modal--small Modal--fullscreen-padded ZS-RefundResultModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-circle-check",
      style: "color:var(--zs-success-34c759, #34c759);margin-right:8px;"
    }), this.fields.title);
  }

  // ─────────────────────────────────────────────────────────────────────
  // 差额 —— **两条数据路径各算各的** (⛔ 不许共用一个)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * `request` 变体的差额 (`meta.amount − meta.system_amount`); null = 没数据 / 相等。
   *
   * ⚠️ 读的是 **`result.meta.*`** ⇒ 只服务 `request` 变体。
   */
  requestDelta() {
    const meta = this.result.meta || {};
    const sys = Number(meta.system_amount);
    const applied = Number(meta.amount !== undefined && meta.amount !== null ? meta.amount : this.result.refund_amount);
    if (!isFinite(sys) || !isFinite(applied)) return null;
    const diff = applied - sys;
    if (Math.abs(diff) < 0.005) return null;
    return {
      sys,
      applied,
      diff,
      over: diff > 0,
      under: diff < 0
    };
  }

  /**
   * `approve` 变体的差额 (`refund_amount − system_amount`); null = 没数据 / 相等。
   *
   * ⚠️ 读的是**平铺的** `result.system_amount` / `result.refund_amount`
   *   ⇒ ⛔ **不许**复用 `requestDelta()` (它读 `meta.*` —— 另一条数据路径)。
   * ⚠️ `system_amount` **可能是 null** (后端 `computeRefund()` 抛异常时) ⇒ 直接返回 null
   *   (⛔ 不许显示 ¥0 / ¥NaN / undefined)。
   */
  approveDelta() {
    const result = this.result || {};
    if (result.system_amount === null || result.system_amount === undefined) return null;
    const sys = Number(result.system_amount);
    const applied = Number(result.refund_amount);
    if (!isFinite(sys) || !isFinite(applied)) return null;
    const diff = applied - sys;
    if (Math.abs(diff) < 0.005) return null;
    return {
      sys,
      applied,
      diff,
      over: diff > 0,
      under: diff < 0
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // 字段映射 —— 一条路径一份 (⛔ render 里不写 variant 三元)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * `request` 变体 (「申请已提交」) 的字段映射。
   *
   * ⛔ **存量口径一个字都不许改** —— 读法逐字照抄改前的 `content()`。
   */
  requestView() {
    const card = this.card || {};
    const result = this.result || {};
    const meta = result.meta || {};
    const finalAmount = meta.amount !== undefined && meta.amount !== null ? meta.amount : result.refund_amount;
    const systemAmount = meta.system_amount;
    const tierOrder = meta.tier_order;
    const delta = this.requestDelta();
    const studentName = card.student_display_name || '学员';
    const rows = [{
      k: '系统核算金额',
      v: '¥' + formatAmount(systemAmount)
    },
    // 差额行 (设计稿 L1154-1157): 只在**有差额**时出现, 紧跟在「系统核算金额」行下面
    {
      k: '退费原因',
      v: meta.reason_label || '—'
    }, {
      k: '审批档',
      kind: 'tier',
      label: meta.approval_label || '—',
      tierOrder: tierOrder
    }, {
      k: '发起时间',
      v: result.refund_at || '—'
    }, {
      k: '关联卡',
      kind: 'card',
      cardName: card.template_name || `学员卡 #${card.id}`,
      sub: `${studentName} · 剩余 ${card.remaining_lessons} 课时`
    }];
    if (delta) {
      // ⛔ 差额行必须**紧跟**「系统核算金额」行 (设计稿 L1154-1157)
      rows.splice(1, 0, {
        k: '差额',
        kind: 'delta',
        over: delta.over,
        diff: delta.diff
      });
    }
    return {
      title: '退卡申请已提交',
      receiptOk: false,
      finalAmount: finalAmount,
      sub: delta ? '系统核算 ¥' + formatAmount(systemAmount) + ' · ' + (delta.over ? '多退' : '少退') + ' ¥' + formatDelta(delta.diff) : '系统核算 ¥' + formatAmount(systemAmount) + '，最终以填写金额为准',
      rows: rows,
      state: null,
      hint: '已提交，等待财务审批。审批通过后这张卡会作废并清零剩余课时。'
    };
  }

  /**
   * `approve` 变体 (「审批通过」回执) 的字段映射。
   *
   * ⛔ 全部读**平铺的** `result.*` (审批端点的返回体) —— ⛔ 不许读 `result.meta.*`。
   */
  approveView() {
    const card = this.card || {};
    const result = this.result || {};

    // 最终打款金额 = **发起时冻结**的申请额 (⛔ 不许拿 system_amount 当打款额)
    const finalAmount = result.refund_amount;
    // ⚠️ system_amount **可能是 null** ⇒ 保持 null (⛔ 不许填 0)
    const systemAmount = result.system_amount === undefined ? null : result.system_amount;
    const delta = this.approveDelta();
    const clearedLessons = Number(result.cleared_lessons);
    const cancelledLessons = Number(result.cancelled_lessons);
    return {
      title: '退卡已通过',
      receiptOk: true,
      finalAmount: finalAmount,
      // ⛔ system_amount 为 null ⇒ **不显示**「系统核算 ¥X」那一支 (不许显示 ¥0 / ¥NaN / undefined)
      sub: systemAmount === null ? '最终打款金额以审批通过金额为准' : delta ? '系统核算 ¥' + formatAmount(systemAmount) + ' · ' + (delta.over ? '多退' : '少退') + ' ¥' + formatDelta(delta.diff) : '系统核算 ¥' + formatAmount(systemAmount) + '，最终以填写金额为准',
      rows: [{
        k: '系统核算金额',
        v: systemAmount === null ? '—' : '¥' + formatAmount(systemAmount)
      }, {
        k: '退费原因',
        v: result.reason_label || '—'
      }, {
        k: '审批档',
        kind: 'tier',
        label: result.approval_label || '—',
        tierOrder: result.tier_order
      }, {
        k: '审批人',
        kind: 'approver',
        name: result.approver_name || '—',
        role: result.approver_role || ''
      }, {
        k: '审批时间',
        v: result.refund_settled_at || '—'
      }, {
        k: '关联卡',
        kind: 'card',
        cardName: result.card_name || `学员卡 #${card.id}`,
        sub: `${result.student_name || '—'} · 剩余 0 课时`
      }],
      state: {
        clearedLessons: isFinite(clearedLessons) ? clearedLessons : 0,
        cancelledLessons: isFinite(cancelledLessons) ? cancelledLessons : 0
      },
      hint: '审批已完成并落库。财务打款请以「最终打款金额」为准；如需复核，可在操作日志里查这笔审批记录。'
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────────────────────────────────

  /**
   * 明细行右边的值 (⛔ 每个 render **现建** JSX —— 见文件头「Mithril vnode 复用」说明)。
   */
  renderRowValue(row) {
    switch (row.kind) {
      case 'tier':
        return m("span", {
          className: 'ZS-RefundModal-tier ' + tierClassName(row.tierOrder)
        }, row.label);
      case 'delta':
        return m("span", {
          className: "ZS-RefundModal-delta"
        }, m("i", {
          className: "fas fa-circle-exclamation"
        }), row.over ? '多退' : '少退', " \xA5", formatDelta(row.diff));
      case 'card':
        return m('[', null, row.cardName, m("span", {
          className: "sub"
        }, row.sub));
      case 'approver':
        return m('[', null, row.name, row.role ? m("span", {
          className: "sub"
        }, row.role) : null);
      default:
        return row.v;
    }
  }

  /** 状态变更说明 (⛔ 只有 `approve` 变体有 —— `state` 为 null 时整块不渲染) */
  stateBlock() {
    const st = this.fields.state;
    if (!st) return null;
    const cleared = st.clearedLessons;
    const cancelled = st.cancelledLessons;
    return m("div", {
      className: "ZS-RefundModal-state"
    }, m("i", {
      className: "fas fa-circle-check"
    }), m("div", null, cleared > 0 ? m('[', null, "\u8BE5\u5361\u5DF2\u4F5C\u5E9F\uFF0C\u5269\u4F59 ", m("b", null, cleared), " \u8BFE\u65F6\u5DF2\u6E05\u96F6") : m('[', null, "\u8BE5\u5361\u5DF2\u4F5C\u5E9F\uFF08\u5269\u4F59\u8BFE\u65F6\u672C\u5C31\u4E3A 0\uFF09"), "\uFF0C\u540C\u65F6\u53D6\u6D88\u4E86 ", m("b", null, cancelled), " \u8282\u672A\u4E0A\u8BFE\u6B21\u3002", m("span", {
      className: "sub"
    }, "\u5361\u72B6\u6001 = \u4F5C\u5E9F\uFF08refunded\uFF09\xB7 \u5269\u4F59\u8BFE\u65F6 ", cleared, " \u2192 0 \xB7 \u672A\u4E0A\u8BFE\u6B21 ", cancelled, " \u8282\u5DF2\u53D6\u6D88\uFF08\u4E0D\u6263\u8BFE\u3001\u4E0D\u9000\u6B3E\uFF09")));
  }
  content() {
    const v = this.fields;
    const receiptClass = v.receiptOk ? 'ZS-RefundModal-receipt ok' : 'ZS-RefundModal-receipt';
    return m('[', null, m("div", {
      className: "ZS-RefundModal-body"
    }, m("div", {
      className: receiptClass
    }, m("div", {
      className: "rc-label"
    }, "\u6700\u7EC8\u6253\u6B3E\u91D1\u989D"), m("div", {
      className: "rc-amount"
    }, "\xA5", formatAmount(v.finalAmount)), m("div", {
      className: "rc-sub"
    }, v.sub)), this.stateBlock(), m("div", {
      className: "ZS-RefundModal-rows"
    }, v.rows.map((row, i) => m("div", {
      className: "rrow",
      key: i
    }, m("span", {
      className: "rk"
    }, row.k), m("span", {
      className: "rv"
    }, this.renderRowValue(row))))), m("div", {
      className: "ZS-RefundModal-hint"
    }, m("i", {
      className: "fas fa-info-circle"
    }), m("div", null, v.hint))), m("div", {
      className: "Modal-footer"
    }, m(Button, {
      className: "Button Button--primary ZS-RefundResultModal-submit",
      onclick: () => this.hide()
    }, "\u77E5\u9053\u4E86")));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/RefundResultModal', RefundResultModal);